import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import {
  downloadDynSupportFile,
  fetchDynSupportLibraryDay,
  fetchDynSupportLibrarySummary,
  type DynSupportLocationFile,
} from 'src/tecnica/modules/consolidate_files/tam/tamViewerService';
import {
  buildTamYearSummaryFromCounts,
  currentYear,
  formatDayLabelShort,
  yearDateRange,
  type TamMonthBucket,
  type TamWeekBucket,
} from './tamYearSummary';
import './styles.scss';

function CountBadge({ count, muted }: { count: number; muted?: boolean }) {
  return (
    <span className={`tam-library__count${muted ? ' tam-library__count--muted' : ''}`}>
      {count}
    </span>
  );
}

function DayRow({
  date,
  count,
  onClick,
}: {
  date: string;
  count: number;
  onClick: () => void;
}) {
  return (
    <button type="button" className="tam-library__row tam-library__row--day" onClick={onClick}>
      <span className="tam-library__chevron">
        <span className="tam-library__chevron-spacer" />
      </span>
      <span className="tam-library__label">{formatDayLabelShort(date)}</span>
      <CountBadge count={count} />
    </button>
  );
}

function WeekBlock({
  week,
  expanded,
  onToggle,
  onDayClick,
}: {
  week: TamWeekBucket;
  expanded: boolean;
  onToggle: () => void;
  onDayClick: (date: string) => void;
}) {
  const hasDays = week.days.length > 0;
  return (
    <div className="tam-library__week">
      <button
        type="button"
        className="tam-library__row tam-library__row--week"
        onClick={hasDays ? onToggle : undefined}
        disabled={!hasDays}
      >
        <span className="tam-library__chevron">
          {hasDays ? (
            expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />
          ) : (
            <span className="tam-library__chevron-spacer" />
          )}
        </span>
        <span className="tam-library__label">{week.label}</span>
        <CountBadge count={week.count} />
      </button>
      {expanded &&
        week.days.map((day) => (
          <DayRow
            key={day.date}
            date={day.date}
            count={day.count}
            onClick={() => onDayClick(day.date)}
          />
        ))}
    </div>
  );
}

function MonthBlock({
  month,
  expanded,
  expandedWeeks,
  onToggleMonth,
  onToggleWeek,
  onDayClick,
}: {
  month: TamMonthBucket;
  expanded: boolean;
  expandedWeeks: Set<string>;
  onToggleMonth: () => void;
  onToggleWeek: (weekId: string) => void;
  onDayClick: (date: string) => void;
}) {
  const hasWeeks = month.weeks.length > 0;
  return (
    <div className={`tam-library__month${month.count === 0 ? ' tam-library__month--empty' : ''}`}>
      <button
        type="button"
        className="tam-library__row tam-library__row--month"
        onClick={hasWeeks ? onToggleMonth : undefined}
        disabled={!hasWeeks}
      >
        <span className="tam-library__chevron">
          {hasWeeks ? (
            expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />
          ) : (
            <span className="tam-library__chevron-spacer" />
          )}
        </span>
        <span className="tam-library__label tam-library__label--month">{month.label}</span>
        <CountBadge count={month.count} muted={month.count === 0} />
      </button>
      {expanded &&
        month.weeks.map((week) => (
          <WeekBlock
            key={week.id}
            week={week}
            expanded={expandedWeeks.has(week.id)}
            onToggle={() => onToggleWeek(week.id)}
            onDayClick={onDayClick}
          />
        ))}
    </div>
  );
}

function formatDayHeader(isoDate: string): string {
  const d = new Date(`${isoDate}T12:00:00`);
  const weekday = d.toLocaleDateString('es-MX', { weekday: 'long' });
  const full = d.toLocaleDateString('es-MX', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)}, ${full}`;
}

function displayFileName(fileName: string): string {
  return fileName.replace(/^(Echometer|Leuter)_/, '').replace(/^\d+_/, '');
}

function TamDayDownloadDialog({
  open,
  date,
  items,
  dayLoading,
  downloadingId,
  onClose,
  onDownload,
}: {
  open: boolean;
  date: string | null;
  items: DynSupportLocationFile[];
  dayLoading: boolean;
  downloadingId: string | null;
  onClose: () => void;
  onDownload: (item: DynSupportLocationFile) => void;
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Box>
          <Typography variant="h6" component="div">
            {date ? formatDayHeader(date) : 'Archivos TAM'}
          </Typography>
          {items.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              {items.length} archivo{items.length === 1 ? '' : 's'} · descarga directa
            </Typography>
          )}
        </Box>
        <IconButton aria-label="Cerrar" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {dayLoading && items.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CircularProgress size={22} />
          </Box>
        ) : items.length === 0 ? (
          <Typography color="text.secondary">Sin archivos para este día.</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {items.map((item) => (
              <Box
                key={item.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1.25,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <InsertDriveFileOutlinedIcon fontSize="small" color="action" />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap title={item.fileName}>
                    {displayFileName(item.fileName)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.wellNames?.length
                      ? `${item.wellNames.length} pozo(s) en el archivo`
                      : 'TAM'}
                  </Typography>
                </Box>
                <Tooltip title="Descargar TAM">
                  <span>
                    <IconButton
                      color="primary"
                      disabled={downloadingId != null}
                      onClick={() => onDownload(item)}
                    >
                      {downloadingId === item.id ? (
                        <CircularProgress size={18} />
                      ) : (
                        <DownloadIcon />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function TamLibraryDownload() {
  const [year, setYear] = useState(currentYear());
  const [fromDate, setFromDate] = useState(yearDateRange(currentYear()).fromDate);
  const [toDate, setToDate] = useState(yearDateRange(currentYear()).toDate);
  const [menuYear, setMenuYear] = useState(currentYear());
  const [menuFromDate, setMenuFromDate] = useState(yearDateRange(currentYear()).fromDate);
  const [menuToDate, setMenuToDate] = useState(yearDateRange(currentYear()).toDate);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const filterMenuOpen = Boolean(filterAnchorEl);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [dailyCounts, setDailyCounts] = useState<Map<string, number>>(new Map());
  const [dayItemsCache, setDayItemsCache] = useState<Map<string, DynSupportLocationFile[]>>(new Map());
  const dayItemsCacheRef = useRef(dayItemsCache);
  dayItemsCacheRef.current = dayItemsCache;
  const [dayLoading, setDayLoading] = useState(false);
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());
  const [expandedWeeks, setExpandedWeeks] = useState<Set<string>>(new Set());
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const yearOptions = useMemo(() => {
    const y = currentYear();
    return Array.from({ length: 6 }, (_, i) => y - i);
  }, []);

  const applyMenuYear = (nextYear: number) => {
    const range = yearDateRange(nextYear);
    setMenuYear(nextYear);
    setMenuFromDate(range.fromDate);
    setMenuToDate(range.toDate);
  };

  const openFilterMenu = (event: MouseEvent<HTMLElement>) => {
    setMenuYear(year);
    setMenuFromDate(fromDate);
    setMenuToDate(toDate);
    setFilterAnchorEl(event.currentTarget);
  };

  const closeFilterMenu = () => setFilterAnchorEl(null);

  const handleApplyFilters = () => {
    if (!menuFromDate || !menuToDate || menuFromDate > menuToDate) {
      setError('Seleccione un rango de fechas válido.');
      return;
    }
    setYear(menuYear);
    setFromDate(menuFromDate);
    setToDate(menuToDate);
    closeFilterMenu();
  };

  const handleResetFilters = () => {
    const y = currentYear();
    const range = yearDateRange(y);
    setMenuYear(y);
    setMenuFromDate(range.fromDate);
    setMenuToDate(range.toDate);
    setYear(y);
    setFromDate(range.fromDate);
    setToDate(range.toDate);
    closeFilterMenu();
  };

  const load = useCallback(async () => {
    if (!fromDate || !toDate || fromDate > toDate) {
      setError('Seleccione un rango de fechas válido.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const summary = await fetchDynSupportLibrarySummary({
        fromDate,
        toDate,
        type: 'TAM',
      });
      setTotal(summary.total);
      setDailyCounts(new Map(summary.dailyCounts.map(({ date, count }) => [date, count])));
      setDayItemsCache(new Map());
      setExpandedMonths(new Set());
      setExpandedWeeks(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar librería TAM');
      setTotal(0);
      setDailyCounts(new Map());
      setDayItemsCache(new Map());
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    void load();
  }, [fromDate, toDate, load]);

  useEffect(() => {
    if (!selectedDay) return;
    if (dayItemsCacheRef.current.has(selectedDay)) return;

    let cancelled = false;
    setDayLoading(true);
    void fetchDynSupportLibraryDay(selectedDay, 'TAM')
      .then((rows) => {
        if (cancelled) return;
        setDayItemsCache((prev) => new Map(prev).set(selectedDay, rows));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Error al cargar archivos del día');
      })
      .finally(() => {
        if (!cancelled) setDayLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedDay]);

  const summary = useMemo(
    () => buildTamYearSummaryFromCounts(dailyCounts, year, fromDate, toDate),
    [dailyCounts, year, fromDate, toDate],
  );

  const daysWithTam = useMemo(
    () => summary.months.reduce((sum, m) => sum + m.weeks.reduce((s, w) => s + w.days.length, 0), 0),
    [summary],
  );

  const dayItems = selectedDay ? dayItemsCache.get(selectedDay) ?? [] : [];

  const toggleMonth = (monthKey: string) => {
    setExpandedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(monthKey)) next.delete(monthKey);
      else next.add(monthKey);
      return next;
    });
  };

  const toggleWeek = (weekId: string) => {
    setExpandedWeeks((prev) => {
      const next = new Set(prev);
      if (next.has(weekId)) next.delete(weekId);
      else next.add(weekId);
      return next;
    });
  };

  const expandAllMonths = () => {
    setExpandedMonths(new Set(summary.months.filter((m) => m.count > 0).map((m) => m.monthKey)));
  };

  const collapseAll = () => {
    setExpandedMonths(new Set());
    setExpandedWeeks(new Set());
  };

  const handleDownload = async (item: DynSupportLocationFile) => {
    setDownloadingId(item.id);
    try {
      await downloadDynSupportFile(item.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al descargar el archivo');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Box className="tam-library">
      <Box className="tam-library__toolbar">
        <Typography variant="h5" color="primary" sx={{ fontWeight: 700 }}>
          Librería TAM
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="body2" color="text.secondary" className="tam-library__filter-summary">
            {year} · {fromDate} — {toDate}
          </Typography>
          <IconButton
            size="medium"
            color="primary"
            sx={{ border: '1px solid #468decff', borderRadius: 2 }}
            onClick={openFilterMenu}
            aria-label="Filtros de periodo"
          >
            <FilterAltIcon fontSize="small" />
          </IconButton>
          <Menu
            id="tam-library-filter-menu"
            anchorEl={filterAnchorEl}
            open={filterMenuOpen}
            onClose={closeFilterMenu}
            MenuListProps={{ 'aria-labelledby': 'tam-library-filter-button' }}
            PaperProps={{ sx: { minWidth: 320 } }}
          >
            <Box className="tam-library__filter-menu-title">Periodo de búsqueda</Box>
            {yearOptions.map((y) => (
              <MenuItem key={y} onClick={() => applyMenuYear(y)} disableRipple>
                <Box
                  className={`tam-library__filter-menu-item${menuYear === y ? ' tam-library__filter-menu-item--active' : ''}`}
                >
                  Año {y}
                </Box>
              </MenuItem>
            ))}
            <MenuItem disabled sx={{ opacity: '1 !important', py: 0.5 }}>
              <Typography variant="caption" color="text.disabled" sx={{ width: '100%', textAlign: 'center' }}>
                ________________________________________
              </Typography>
            </MenuItem>
            <Box
              className="tam-library__filter-menu-fields"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <TextField
                label="Desde"
                type="date"
                size="small"
                fullWidth
                value={menuFromDate}
                onChange={(e) => setMenuFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Hasta"
                type="date"
                size="small"
                fullWidth
                value={menuToDate}
                onChange={(e) => setMenuToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Button variant="contained" fullWidth onClick={handleApplyFilters} disabled={loading}>
                Buscar
              </Button>
            </Box>
            <MenuItem disabled sx={{ opacity: '1 !important', py: 0.5 }}>
              <Typography variant="caption" color="text.disabled" sx={{ width: '100%', textAlign: 'center' }}>
                ________________________________________
              </Typography>
            </MenuItem>
            <MenuItem onClick={handleResetFilters}>
              <IconButton size="small" color="primary">
                <RestartAltOutlinedIcon fontSize="small" />
              </IconButton>
              Resetear filtros
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      <Box className="tam-library__header">
        <Typography variant="body2">
          <strong>Librería {year}</strong>
          <span className="tam-library__sep">·</span>
          {total} TAM
          <span className="tam-library__sep">·</span>
          {daysWithTam} días con archivos
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Button size="small" onClick={expandAllMonths}>
            Expandir
          </Button>
          <Button size="small" onClick={collapseAll}>
            Contraer
          </Button>
          <IconButton size="small" onClick={() => void load()} disabled={loading} aria-label="Actualizar">
            {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
          </IconButton>
        </Box>
      </Box>

      <Box className="tam-library__month-grid">
        {summary.months.map((month) => {
          const monthNum = Number(month.monthKey.slice(5, 7));
          const abbr = new Date(summary.year, monthNum - 1, 1).toLocaleDateString('es-MX', {
            month: 'short',
          });
          return (
            <div
              key={month.monthKey}
              className={`tam-library__month-cell${month.count > 0 ? ' has-data' : ''}`}
              title={`${month.label}: ${month.count} TAM`}
            >
              <span className="tam-library__month-cell__abbr">{abbr}</span>
              <strong>{month.count}</strong>
            </div>
          );
        })}
      </Box>

      <Box className="tam-library__tree">
        {loading && dailyCounts.size === 0 && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {error && !loading && (
          <Typography color="error" sx={{ py: 2 }}>
            {error}
          </Typography>
        )}
        {!loading && !error && total === 0 && (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            No hay TAM registrados en el periodo seleccionado.
          </Typography>
        )}
        {summary.months.map((month) => (
          <MonthBlock
            key={month.monthKey}
            month={month}
            expanded={expandedMonths.has(month.monthKey)}
            expandedWeeks={expandedWeeks}
            onToggleMonth={() => toggleMonth(month.monthKey)}
            onToggleWeek={toggleWeek}
            onDayClick={setSelectedDay}
          />
        ))}
      </Box>

      <TamDayDownloadDialog
        open={selectedDay != null}
        date={selectedDay}
        items={dayItems}
        dayLoading={dayLoading}
        downloadingId={downloadingId}
        onClose={() => {
          if (!downloadingId) setSelectedDay(null);
        }}
        onDownload={(item) => void handleDownload(item)}
      />
    </Box>
  );
}

export default TamLibraryDownload;
